import React, { useState, useCallback, useEffect } from 'react';
import { Badge } from '../StatusBadge';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { JobXFeature } from '../../types';
import RelativeTime from '../RelativeTime';
import { apiService } from '../../services/api';
import { TableCard, TableHeader, TableViewport, SortableTh, EmptyRow, thClass, tdClass, SortOrder } from '../table/TableKit';

interface Props {
  jobFeatures: JobXFeature[];
  changes: Map<string, boolean>;
  onFeatureChange: (jobId: string, featureKey: string, isEnabled: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  total: number;
  offset: number;
  pageSize: number;
  onOffsetChange: (offset: number) => void;
  onPageSizeChange: (size: number) => void;
  sort: string;
  order: SortOrder;
  onSort: (sort: string, order: SortOrder) => void;
}

const featureColumns = [
  { key: 'analyzer_web', label: 'Analyzer WEB' },
];

const JobFeaturesTab: React.FC<Props> = ({ jobFeatures, changes, onFeatureChange, searchQuery, onSearchChange, total, offset, pageSize, onOffsetChange, onPageSizeChange, sort, order, onSort }) => {
  const [logContent, setLogContent] = useState<string>('');
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [logJobId, setLogJobId] = useState<string>('');

  // Helper function to get effective value including pending changes
  const getEffectiveValue = useCallback((item: JobXFeature, featureKey: string): boolean => {
    const changeKey = `${item.job_id}#${featureKey}`;
    if (changes.has(changeKey)) {
      return changes.get(changeKey)!;
    }
    // Access the feature value dynamically
    return (item as any)[featureKey] || false;
  }, [changes]);

  const hasChange = (item: JobXFeature): boolean => {
    // Check if any feature for this job has been changed
    return featureColumns.some(({ key }) => {
      const featureKey = key;
      const changeKey = `${item.job_id}#${featureKey}`;
      return changes.has(changeKey);
    });
  };


  useEffect(() => {
    if (!logJobId) return;
    // Dropped when the modal closes or another log opens, so a slow old response cannot win.
    let current = true;
    const fetchLog = async () => {
      setLogLoading(true);
      setLogError(null);
      setLogContent('');
      try {
        const content = await apiService.getJobAnalyzerLog(logJobId);
        if (!current) return;
        setLogContent(content);
      } catch (error: any) {
        if (!current) return;
        if (error.response?.status === 404) {
          setLogError('No analyzer log for this job.');
        } else {
          setLogError('Failed to load analyzer log.');
        }
      } finally {
        if (current) setLogLoading(false);
      }
    };
    fetchLog();
    return () => {
      current = false;
    };
  }, [logJobId]);

  const closeLogModal = () => {
    setLogContent('');
    setLogError(null);
    setLogJobId('');
  };

  return (
    <TableCard>
      <TableHeader
        title="Job Features"
        search={{ value: searchQuery, onChange: onSearchChange }}
        pagination={{
          offset,
          limit: pageSize,
          total,
          onOffsetChange,
          onLimitChange: onPageSizeChange,
        }}
      />

      <TableViewport>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-700">
              {featureColumns.map(({ key, label }) => (
                <SortableTh
                  key={key}
                  label={label}
                  sortKey={key}
                  sort={sort}
                  order={order}
                  onSort={onSort}
                />
              ))}
              <SortableTh
                label="Instance Key"
                sortKey="instance_key"
                sort={sort}
                order={order}
                onSort={onSort}
                firstOrder="asc"
              />
              <th className={thClass}>Job ID</th>
              <SortableTh label="Operation" sortKey="operation" sort={sort} order={order} onSort={onSort} firstOrder="asc" />
              <SortableTh label="Status" sortKey="status" sort={sort} order={order} onSort={onSort} firstOrder="asc" />
              <SortableTh label="License Tokens" sortKey="license_tokens" sort={sort} order={order} onSort={onSort} />
              <SortableTh
                label="Created"
                sortKey="created_at"
                sort={sort}
                order={order}
                onSort={onSort}
              />
              <th className={thClass}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {jobFeatures.length === 0 ? (
              <EmptyRow colSpan={8} text="No job features found." />
            ) : (
              jobFeatures.map((item) => (
                <tr
                  key={item.job_id}
                  className={`hover:bg-body-bg transition-colors ${hasChange(item) ? 'bg-yellow-900/20' : ''}`}
                >
                  {featureColumns.map(({ key }) => (
                    <td key={key} className={tdClass}>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={getEffectiveValue(item, key)}
                          onChange={(e) => onFeatureChange(item.job_id, key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </td>
                  ))}
                  <td className={tdClass}>{item.instance_key}</td>
                  <td className={`${tdClass} font-mono`}>{item.job_id}</td>
                  <td className={tdClass}>{item.operation}</td>
                  <td className={tdClass}>
                    <Badge value={item.status} />
                  </td>
                  <td className={tdClass}>
                    {item.license_tokens !== null ? item.license_tokens.toLocaleString() : '-'}
                  </td>
                  <td className={`${tdClass} text-secondary`}><RelativeTime value={item.created_at} /></td>
                  <td className={tdClass}>
                    <button
                      onClick={() => { setLogLoading(true); setLogJobId(item.job_id); }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-secondary hover:text-primary hover:bg-body-bg rounded transition-colors"
                      title="View analyzer log"
                    >
                      <DocumentTextIcon className="w-4 h-4" />
                      Log
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableViewport>

      {/* Analyzer Log Modal */}
      {logJobId !== '' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeLogModal}>
          <div className="bg-widget-bg border border-secondary rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-secondary">
              <h3 className="text-lg font-semibold">
                Analyzer Log
                <span className="ml-2 text-sm font-mono text-secondary">{logJobId}</span>
              </h3>
              <button
                onClick={closeLogModal}
                aria-label="Close"
                className="text-secondary hover:text-body-color transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {logLoading && (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}
              {logError && (
                <div className="text-red-500 text-center py-8">{logError}</div>
              )}
              {!logLoading && !logError && logContent && (
                <pre className="text-sm font-mono text-body-color whitespace-pre-wrap break-words bg-body-bg p-4 rounded overflow-auto">
                  {logContent}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </TableCard>
  );
};

export default JobFeaturesTab;
